/* The existing Pickeasy app owns availability and its order attributes.
   This adapter confirms the slot and sets Shopify’s native checkout pickup preference.
   Pickeasy still validates and saves its date/time attributes. */
(() => {
  const form = document.querySelector('#cart');
  const panel = form?.querySelector('[data-pickup-confirmation]');
  if (!panel) return;
  const confirm = panel.querySelector('[data-pickup-confirm]');
  const status = panel.querySelector('[data-pickup-status]');
  const error = panel.querySelector('[data-pickup-error]');
  let signature = '';
  let restoredStore = false;
  let preferredStore = form.dataset.previousPickupStore || '';
  try { preferredStore = sessionStorage.getItem('hb-pickup-store') || preferredStore; } catch { /* Storage can be unavailable; server cart remains the fallback. */ }
  let storeChosen = Boolean(preferredStore);
  const storeName = radio => radio.closest('.pickup-location')?.querySelector('label')?.textContent.trim() || '';

  let started = Date.now();
  const value = id => form.querySelector(`#${id}`)?.value.trim() || '';
  const read = () => ({ store: value('order-location'), address: value('order-location-address'), date: value('order-fulfilment-date'), time: value('order-fulfilment-time') });
  function refresh() {
    const radios = [...form.querySelectorAll('input[name="pickup-location-option"]')];
    if (!restoredStore && radios.length && radios.every(radio => radio.closest('.pickup-location')?.hasAttribute('listener'))) {
      restoredStore = true;
      const preferred = radios.find(radio => storeName(radio) === preferredStore);
      // Re-enter through the app's control so its availability rules run for this store.
      if (preferred && !preferred.checked) { preferred.click(); return; }
    }
    const slot = read();
    const widget = form.querySelector('#order-slots');
    if (widget && widget.nextElementSibling !== panel) widget.after(panel);
    if (slot.store === 'Eastern Creek Quarter') slot.address = 'Shop T33, 159 Rooty Hill Road South, Eastern Creek NSW 2766';
    if (slot.store === 'Windsor') slot.address = 'Shop 3/70 George Street, Windsor NSW 2756';
    const current = JSON.stringify(slot);
    if (signature !== current) {
      signature = current;
      confirm.checked = false;
      error.hidden = true;
    }
    const dirty = [...form.querySelectorAll('[name="updates[]"]')].some(input => input.value !== input.defaultValue);
    const selectedRadio = radios.find(radio => radio.checked);
    const selectedStore = selectedRadio ? storeName(selectedRadio) : '';
    const ready = storeChosen && selectedStore === slot.store && slot.store && slot.address && slot.date && slot.time && !dirty;
    if (dirty) confirm.checked = false;
    confirm.disabled = !ready;
    confirm.value = ready ? `${slot.store} — ${slot.address} — ${slot.date}, ${slot.time}` : '';
    const nextStatus = !storeChosen ? 'Please choose Eastern Creek Quarter or Windsor above, even if a store is already highlighted. Then choose your date and time.' : dirty ? 'Your quantities have changed. Select Update cart to recheck pickup availability before confirming.' : ready ? `Pickup: ${confirm.value}` : slot.store ? `Selected store: ${slot.store} — ${slot.address}. Choose an available date and time, then confirm your selection.` : Date.now() - started > 15000 ? 'Pickup scheduling has not loaded. Please refresh the page to try again, or contact the bakery before ordering.' : 'Loading available pickup locations and times…';
    if (status.textContent !== nextStatus) status.textContent = nextStatus;
    // Repair the vendor labels' mismatched for attributes without changing its controls.
    form.querySelectorAll('.pickup-location').forEach(location => {
      const radio = location.querySelector('input[type="radio"]');
      if (radio) location.querySelectorAll('label').forEach(label => { label.htmlFor = radio.id; });
    });
    form.querySelectorAll('.neo-cell-enabled, .neo-paginate:not(.neo-paginate-disabled), .neo-back, .neo-close').forEach(control => {
      control.tabIndex = 0;
      control.setAttribute('role', 'button');
      const text = control.textContent.trim();
      const month = form.querySelector('.neo-date-grid .neo-center')?.textContent.replace(/[<>]/g, '').trim() || '';
      control.setAttribute('aria-label', control.classList.contains('neo-close') ? 'Close calendar' : control.classList.contains('neo-back') ? 'Choose a different date' : control.classList.contains('neo-paginate') ? (text === '<' ? 'Previous month' : 'Next month') : `Choose ${text}${control.closest('.neo-date-grid') ? ' ' + month : ''}`);
    });
    form.querySelectorAll('.neo-date-grid tr:nth-child(2) td').forEach(day => {
      const text = day.textContent.trim();
      if (text.length > 3) { day.setAttribute('aria-label', text); day.textContent = text.slice(0, 3); }
    });
    const date = form.querySelector('.neo-input');
    if (date && !date.getAttribute('aria-label')) date.setAttribute('aria-label', 'Choose pickup date and time');
  }
  function validate(event) {
    refresh();
    if (confirm.disabled || !confirm.checked) {
      event.preventDefault();
      event.stopImmediatePropagation();
      error.hidden = false;
      error.textContent = confirm.disabled ? 'Choose an available pickup store, date and time before continuing.' : 'Please confirm your pickup store, date and time before continuing.';
      (confirm.disabled ? form.querySelector('.neo-input') || status : confirm).focus();
      panel.scrollIntoView({ block: 'center', behavior: 'auto' });
      return false;
    }
    return true;
  }
  const pickupLocations = { 'Eastern Creek Quarter': '82522571068', Windsor: '112167911740' };
  let syncingPickup = false;
  let approvedHandoff = '';
  async function checkout(event) {
    if (!validate(event)) return;
    const selection = JSON.stringify(read());
    if (approvedHandoff === selection) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (syncingPickup) return;
    syncingPickup = true;
    const button = form.querySelector('#checkout');
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Confirming pickup store…';
    button.setAttribute('aria-busy', 'true');
    error.hidden = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const locationId = pickupLocations[read().store];
      if (!locationId) throw new Error('Unknown pickup location');
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { signal: controller.signal });
      if (!cartResponse.ok) throw new Error('Unable to read cart');
      const cart = await cartResponse.json();
      if (!cart.token || !cart.item_count) throw new Error('Empty cart');
      // Numeric location handles are required here, not GraphQL location GIDs.
      // Update the existing cart; do not create a second cart or change buyer details.
      const response = await fetch('/api/2026-10/graphql.json', {
        method: 'POST', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          query: 'mutation PickupPreference($id: ID!, $buyer: CartBuyerIdentityInput!) { cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: $buyer) { cart { buyerIdentity { preferences { delivery { pickupHandle deliveryMethod } } } } userErrors { message } } }',
          variables: { id: `gid://shopify/Cart/${cart.token}`, buyer: { preferences: { delivery: { deliveryMethod: ['PICK_UP'], pickupHandle: [locationId] } } } }
        })
      });
      if (!response.ok) throw new Error('Unable to save pickup');
      const result = await response.json();
      const update = result.data?.cartBuyerIdentityUpdate;
      const preference = update?.cart?.buyerIdentity?.preferences?.delivery;
      if (result.errors?.length || update?.userErrors?.length || !preference?.pickupHandle?.includes(locationId) || !preference.deliveryMethod?.includes('PICK_UP')) throw new Error('Pickup not confirmed');
      refresh();
      if (selection !== JSON.stringify(read()) || confirm.disabled || !confirm.checked) throw new Error('Selection changed');
      // Re-enter the existing app checkout flow only after Shopify confirms the store.
      approvedHandoff = selection;
      button.disabled = false;
      button.click();
    } catch {
      approvedHandoff = '';
      error.textContent = 'We couldn’t confirm your pickup store for checkout. Check your store, date and time, then try Continue to checkout again.';
      error.hidden = false;
      error.tabIndex = -1;
      error.focus();
    } finally {
      clearTimeout(timeout);
      syncingPickup = false;
      button.disabled = false;
      button.textContent = label;
      button.removeAttribute('aria-busy');
      // Allow the synchronous click/submit pair, but always revalidate a later attempt.
      setTimeout(() => { approvedHandoff = ''; }, 0);
    }
  }
  // Capture before Pickeasy. Its slot validation and attribute submission still run.
  document.addEventListener('click', event => {
    if (event.target.closest('#checkout')) checkout(event);
  }, true);
  form.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') checkout(event);
  }, true);
  form.addEventListener('input', event => { if (event.target.name === 'updates[]') refresh(); });
  form.addEventListener('click', event => {
    const radio = event.target.closest('.pickup-location')?.querySelector('input[name="pickup-location-option"]');
    if (!radio) return;
    // Pickeasy handles label/container clicks itself and can suppress native change.
    // Remember the choice from the user interaction, not only the change event.
    preferredStore = storeName(radio);
    storeChosen = true;
    restoredStore = true;
    confirm.checked = false;
    try { sessionStorage.setItem('hb-pickup-store', preferredStore); } catch { /* Server cart remains a fallback. */ }
    setTimeout(refresh, 0);
  }, true);
  form.addEventListener('change', event => {
    if (event.target.name === 'pickup-location-option' || event.target.name === 'updates[]') confirm.checked = false;
    if (event.target.name === 'pickup-location-option') {
      storeChosen = true;
      preferredStore = storeName(event.target);
      restoredStore = true;
      try { sessionStorage.setItem('hb-pickup-store', preferredStore); } catch { /* Preserve normal selection without storage. */ }
    }
  });
  form.addEventListener('keydown', event => {
    if (event.key === 'Escape' && event.target.closest('datetime-slot-picker')) {
      form.querySelector('.neo-close')?.click();
      form.querySelector('.neo-input')?.focus();
    }
    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('.neo-input, .neo-cell-enabled, .neo-paginate:not(.neo-paginate-disabled), .neo-back, .neo-close')) {
      event.preventDefault();
      event.target.click();
      setTimeout(() => { refresh(); form.querySelector('.neo-cell-enabled')?.focus(); }, 50);
    }
  });
  confirm.addEventListener('change', () => { error.hidden = true; });
  // The vendor updates hidden input properties without emitting DOM change events.
  let timer;
  const start = () => { clearInterval(timer); refresh(); timer = setInterval(refresh, 500); };
  window.addEventListener('pagehide', () => clearInterval(timer));
  window.addEventListener('pageshow', () => { confirm.checked = false; start(); });
  start();
})();
